import { useEffect } from 'react';
import { baseApi } from '../store/api/baseApi';
import { IS_TENANT_MODE } from '../config/tenantMode';

export type PublicBranding = {
  brandName: string;
  logoUrl: string;
  websiteUrl?: string;
  supportTelegram?: string | null;
  accentColor?: string | null;
};

const TENANT_DEFAULT_NAME = 'Admin Panel';
const MASTER_DEFAULT_NAME = 'Rocket Proxy';
const MASTER_DEFAULT_LOGO =
  'https://res.cloudinary.com/dwhpimotj/image/upload/v1758543752/logo_pnm9g9.png';
/** Professional teal — default merchant admin identity */
export const TENANT_DEFAULT_ACCENT = '#34D3C0';
export const TENANT_DEFAULT_PRIMARY = '#060B14';

export const brandingApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getPublicBranding: builder.query<{ status: string; data: PublicBranding }, void>({
      query: () => ({ url: '/public/branding', method: 'GET' }),
      providesTags: ['Branding'],
    }),
  }),
});

export const { useGetPublicBrandingQuery } = brandingApi;

function applyAccent(accent: string | null | undefined) {
  const root = document.documentElement;
  const color = accent && /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(accent)
    ? accent
    : TENANT_DEFAULT_ACCENT;
  root.style.setProperty('--brand-accent', color);
  root.style.setProperty('--brand-cyan', color);
}

/** Resolve display brand for header/login — tenant uses saved branding, never Rocket Proxy. */
export function useDisplayBranding() {
  const { data, isLoading } = useGetPublicBrandingQuery(undefined, {
    skip: !IS_TENANT_MODE,
  });

  const branding = data?.data;
  const brandName = IS_TENANT_MODE
    ? (branding?.brandName?.trim() || TENANT_DEFAULT_NAME)
    : MASTER_DEFAULT_NAME;
  const logoUrl = IS_TENANT_MODE
    ? (branding?.logoUrl?.trim() || '')
    : MASTER_DEFAULT_LOGO;
  const accentColor = IS_TENANT_MODE
    ? (branding?.accentColor || TENANT_DEFAULT_ACCENT)
    : null;

  useEffect(() => {
    if (!IS_TENANT_MODE) return;
    document.title = brandName;
  }, [brandName]);

  useEffect(() => {
    if (!IS_TENANT_MODE) return;
    document.documentElement.setAttribute('data-platform', 'tenant');
    document.documentElement.style.setProperty('--brand-primary', TENANT_DEFAULT_PRIMARY);
    applyAccent(accentColor);
  }, [accentColor]);

  return { brandName, logoUrl, accentColor, isLoading, branding };
}
